/**
 * Which conversation `MultiAgentRoute` shows: the lane-v2 session view (the only lane).
 *
 * The decision rules are pure (`services/multi-agent/laneV2/lane.ts`: `routeAction`, `showsLaneV2`);
 * this hook only feeds them the stores and runs the resulting action:
 *
 *   · no active conversation (a new chat)          → R0 says lane v2 → a v2 DRAFT with a client-owned
 *                                                    UUID (pinned only when the first message is sent).
 *   · the active conversation, or a `?session=` link → R1 (only if R0 advertised the read model):
 *                                                    200 → attach as v2; otherwise leave the v2 view.
 *
 * On a server without the read model `active` stays false and nothing can be sent.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { resolveActiveOrgId, useAuthStoreV1 } from "@/stores/authStore_v1";
import { useWeaveSessionStore } from "@/stores/weaveSessionStore";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useLaneV2SessionStore } from "@/stores/laneV2SessionStore";
import { ensureV2Attached, isV2Sourced } from "@/services/multi-agent/laneV2/openV2Session";
import { activeConversationId, newConversationId } from "@/services/multi-agent/sessionIdentity";
import { startNewConversation } from "@/services/multi-agent/openConversation";
import {
  invalidateCapabilities,
  laneForNewConversation,
  resolveCapabilities,
  resolveSessionLane,
  routeAction,
  capsGate,
  consumedAfter,
  linkErrorFor,
  nextReprobeDelayMs,
  onNonV2Resolution,
  LANE_V2_UNAVAILABLE_MESSAGE,
  pendingQuerySession,
  routeSurfaceFor,
  sessionParamAction,
  showsLaneV2,
} from "@/services/multi-agent/laneV2/lane";

export interface UseLaneV2RouteReturn {
  /** The lane-v2 session view is attached (a draft or a pinned session). */
  active: boolean;
  /**
   * The active conversation's lane is not known yet (its R1 probe is in flight, or a history row said it is v2 and the
   * attach has not landed). The composer must NOT send then.
   */
  resolving: boolean;
  /** The lane lookup failed for a conversation known to be v2: show it with a retry; nothing was reset. */
  resolveError: string | null;
  retryResolve: () => void;
  /** R0 is unreachable (5xx / network): "Connecting…", Send waits, and the route asks again. Never pins the old lane. */
  connecting: boolean;
  /** A `?session=` link that could not be opened (404 for another org/user, or a lookup failure); dismissible, never blocks Send. */
  linkError: string | null;
  dismissLinkError: () => void;
}

type Resolution = { sessionId: string; status: "resolving" } | { sessionId: string; status: "error"; message: string };

/**
 * The route effect's dependency list, as VALUES rather than an inline array literal — pulled out so
 * the fix below (`useLaneV2Route.test.ts`) is checkable without rendering anything: a dependency array
 * itself cannot be imported out of a `useEffect` call, but this can, and the hook's own effect uses
 * this function's return value as its real deps array (not a copy of it), so there is exactly one
 * place that says what the effect depends on.
 *
 * `v2SessionId`/`v2Draft` are here alongside `active`/`orgId`/`querySessionId` — see the long
 * comment on the `useEffect` call below for why both are required.
 */
export function routeEffectDeps(input: {
  orgId: string | null;
  active: string | null;
  querySessionId: string | null;
  v2SessionId: string | null;
  v2Draft: boolean;
}): readonly [string | null, string | null, string | null, string | null, boolean] {
  return [input.orgId, input.active, input.querySessionId, input.v2SessionId, input.v2Draft];
}

export function useLaneV2Route(): UseLaneV2RouteReturn {
  // The one shared "actively selected org" rule (`authStore_v1.ts`'s `resolveActiveOrgId`) — also
  // used by `client.ts` and `useMergedSessions.ts`, so a new chat and the history list never disagree
  // about which org a single-org user is in.
  const orgId = useAuthStoreV1((s) => resolveActiveOrgId(s));
  const weave = useWeaveSessionStore(useShallow((s) => ({ sessionId: s.sessionId, current: s.current })));
  const legacy = useMultiAgentStore(useShallow((s) => ({ sessionId: s.sessionId, runId: s.runId })));
  const active = activeConversationId(
    { sessionId: weave.sessionId, current: weave.current },
    { sessionId: legacy.sessionId, runId: legacy.runId, persistedSessionId: null },
  );
  const v2 = useLaneV2SessionStore(useShallow((s) => ({ sessionId: s.sessionId, draft: s.draft })));
  const [searchParams, setSearchParams] = useSearchParams();
  const querySessionId = searchParams.get("session");

  const attachedFor = useRef<string | null>(null);
  const [resolution, setResolution] = useState<Resolution | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [connecting, setConnecting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [link, setLink] = useState<{ sessionId: string; message: string } | null>(null);
  const setLinkError = (message: string | null, sessionId = "") => setLink(message ? { sessionId, message } : null);
  // The `?session=` deep link is consumed once it has been probed: from then on the URL follows the active conversation.
  const consumedRef = useRef<string | null>(null);
  const [consumed, setConsumed] = useState<string | null>(null);
  const consume = (sid: string | null) => {
    consumedRef.current = sid;
    setConsumed(sid);
  };
  // ONE re-probe loop per tab: consecutive misses back off 5 s -> 60 s, and it sleeps while the tab is hidden.
  const missesRef = useRef(0);
  const retryResolve = useCallback(() => {
    // A failed R0 is cached as legacy; a retry must ask again or it re-reads the same failure.
    invalidateCapabilities(orgId);
    missesRef.current = 0;
    consumedRef.current = null; // a retry re-opens a link that failed
    setConsumed(null);
    setLinkError(null);
    setAuthError(null);
    setUnavailable(null);
    setRetryNonce((n) => n + 1);
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let visibilityHandler: (() => void) | null = null;
    const store = useLaneV2SessionStore.getState;
    const key = (sid: string) => `${orgId ?? ""}:${sid}`;
    const s = store();
    if (!querySessionId && consumedRef.current) consume(null);
    const afterOwn = consumedAfter({ consumed: consumedRef.current, querySessionId, active, write: null });
    if (afterOwn !== consumedRef.current) consume(afterOwn);
    const pendingQuery = pendingQuerySession(querySessionId, consumedRef.current);
    const action = routeAction({
      active,
      querySessionId: pendingQuery,
      v2: { sessionId: s.sessionId, draft: s.draft, streaming: s.streamStatus !== "idle" },
      attached: (sid) => attachedFor.current === key(sid),
    });

    /** R0 was unreachable: say so, keep Send closed, and ask again shortly (the caps cache never holds this answer). */
    const awaitConnection = () => {
      setConnecting(true);
      const rerun = () => setRetryNonce((n) => n + 1);
      const delay = nextReprobeDelayMs(missesRef.current++);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (typeof document === "undefined" || !document.hidden) return rerun();
        // Hidden tab: do not poll; ask once when it becomes visible again.
        visibilityHandler = () => {
          if (document.hidden) return;
          document.removeEventListener("visibilitychange", visibilityHandler!);
          visibilityHandler = null;
          rerun();
        };
        document.addEventListener("visibilitychange", visibilityHandler);
      }, delay);
    };
    /** R0 answered; stop here when it is not a usable answer. true = handled. */
    const stopOnCaps = (caps: { connecting: boolean; unauthorized: boolean }): boolean => {
      const gate = capsGate(caps);
      if (gate === "wait") {
        awaitConnection();
        return true;
      }
      missesRef.current = 0;
      if (gate === "unauthorized") {
        setConnecting(false);
        setAuthError("You are signed out, or your account has no access to Weave. Sign in again, then retry.");
        return true;
      }
      setAuthError(null);
      return false;
    };

    if (action.kind === "none") setConnecting(false);
    if (action.kind !== "probe") setResolution(null);
    else setResolution({ sessionId: action.sessionId, status: "resolving" });

    (async () => {
      if (action.kind === "none") {
        if (s.sessionId && !s.draft) attachedFor.current = key(s.sessionId);
        return;
      }
      if (action.kind === "new-conversation") {
        // Send stays closed from the moment R0 is asked (a send before the lane is known would have nowhere to go).
        setConnecting(true);
        const caps = await resolveCapabilities(orgId);
        if (cancelled) return;
        if (stopOnCaps(caps)) return;
        setConnecting(false);
        // A NEW chat starts a v2 draft when the server says v2.
        if (laneForNewConversation(caps) === "v2") {
          setUnavailable(null);
          attachedFor.current = null;
          store().startDraft(newConversationId(), {
            deployMode: caps.deployMode,
            turnAnswersAsk: caps.turnAnswersAsk,
            offline: caps.offline,
            turnAttachments: caps.turnAttachments,
            turnIdeState: caps.turnIdeState,
            branchSave: caps.branchSave,
            liveFrames: caps.liveFrames,
          });
        } else {
          // No lane to start a chat on: say so (a silently closed composer reads as a hang).
          setUnavailable(LANE_V2_UNAVAILABLE_MESSAGE);
          if (store().sessionId) store().reset();
        }
        return;
      }
      if (isV2Sourced(action.sessionId)) {
        // A history row said the server pinned this to v2: no R1 gate (a failed lookup is not evidence otherwise).
        // The single attach owner does the rest.
        setConnecting(true);
        const sourcedCaps = await resolveCapabilities(orgId);
        if (cancelled) return;
        if (stopOnCaps(sourcedCaps)) return;
        setConnecting(false);
        attachedFor.current = key(action.sessionId);
        if (action.adopt) {
          useWeaveSessionStore.getState().adopt(action.sessionId);
        }
        await ensureV2Attached(action.sessionId);
        if (cancelled) return;
        if (pendingQuery === action.sessionId) consume(pendingQuery);
        if (store().sessionId !== action.sessionId) {
          attachedFor.current = null;
          setResolution({
            sessionId: action.sessionId,
            status: "error",
            message: "Could not load this conversation. Check your connection and try again.",
          });
        } else {
          setResolution(null);
        }
        return;
      }
      const resolved = await resolveSessionLane(orgId, action.sessionId);
      if (cancelled) return;
      // R0 itself unreachable (not evidence of a legacy conversation) or 401/403: wait / say so, never fall to legacy.
      if (stopOnCaps(resolved.caps)) return;
      setConnecting(false);
      setResolution(null);
      if (pendingQuery === action.sessionId) {
        consume(pendingQuery);
        setLinkError(resolved.lane === "v2" ? null : linkErrorFor(resolved.failure), pendingQuery);
      }
      if (resolved.lane === "v2") {
        attachedFor.current = key(action.sessionId);
        if (action.adopt) {
          useWeaveSessionStore.getState().adopt(action.sessionId);
        }
        void store().attach(action.sessionId, {
          session: resolved.session,
          deployMode: resolved.caps.deployMode,
          turnAnswersAsk: resolved.caps.turnAnswersAsk,
          offline: resolved.caps.offline,
          turnAttachments: resolved.caps.turnAttachments,
          turnIdeState: resolved.caps.turnIdeState,
          branchSave: resolved.caps.branchSave,
          liveFrames: resolved.caps.liveFrames,
        });
      } else {
        if (store().sessionId && (!action.adopt || store().sessionId === action.sessionId)) {
          // The conversation on screen is not a v2 session: leave the v2 view.
          attachedFor.current = null;
          store().reset();
        }
        // A persisted (non-link) id that is not v2 would leave an empty, unsendable chat: start a fresh one.
        if (onNonV2Resolution({ lane: resolved.lane, fromDeepLink: pendingQuery === action.sessionId }) === "start-new") {
          startNewConversation();
        }
      }
    })();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (visibilityHandler) document.removeEventListener("visibilitychange", visibilityHandler);
    };
    /*
     * `v2.sessionId`/`v2.draft` ARE real dependencies (added alongside the pre-existing ones — bug fix,
     * "New conversation" from an empty v2 draft). `startNewConversation` clears the v2 store via
     * `reset()`, but while a draft has never been sent `active` is already null and `reset()` does not
     * change it (still null), so without these two in the deps this effect would not re-run at all: no
     * new draft gets started and `showsLaneV2` sees `v2.sessionId === null`. (A v2 session that already has messages does not have this problem — closing it
     * changes `active`, which was already a dependency.)
     *
     * This does not start a start→rerun→start loop. Every write this effect makes to the v2 store also
     * lands on `sessionId`/`draft` together (`startDraft` sets both; `attach` sets both; `pinDraft`
     * flips `draft` but also adopts the session into `weaveSessionStore`, which changes `active` in the
     * same tick), so the effect DOES re-run once more after each action — but `routeAction` reads that
     * fresh state and returns `{ kind: "none" }` on the replay: a fresh draft satisfies
     * `v2.sessionId && v2.draft`, and a fresh attach satisfies `attached(target)` (set just before the
     * attach call, or by `startDraft` resetting it to null). So each user action still produces exactly
     * one `startDraft`/`attach` call, just one extra pass through the effect that does nothing.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...routeEffectDeps({ orgId, active, querySessionId, v2SessionId: v2.sessionId, v2Draft: v2.draft }), retryNonce]);

  /*
   * The URL follows the conversation on screen (`sessionParamAction`, one owner; the legacy store no longer strips it):
   * written once THIS tab attached the session, removed for a new chat / legacy conversation. The unconsumed deep link
   * is left alone, so the URL wins on reload and the persisted id only serves a bare /weave.
   */
  const urlAction = sessionParamAction({
    querySessionId,
    consumed,
    active,
    v2: { sessionId: v2.sessionId, draft: v2.draft },
    attachedHere: !!v2.sessionId && attachedFor.current === `${orgId ?? ""}:${v2.sessionId}`,
  });
  const urlActionKey = urlAction ? `${urlAction.kind}:${urlAction.kind === "set" ? urlAction.sessionId : ""}` : "";
  useEffect(() => {
    if (!urlAction) return;
    const next = consumedAfter({ consumed: consumedRef.current, querySessionId, active, write: urlAction });
    if (next !== consumedRef.current) consume(next); // the route's own write is not a deep link
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (urlAction.kind === "set") {
          next.delete("run");
          next.set("session", urlAction.sessionId);
        } else next.delete("session");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlActionKey]);

  const shown = showsLaneV2(active, v2);
  const surface = routeSurfaceFor({ active, shown, v2Sourced: isV2Sourced(active), resolution });
  return {
    active: shown,
    ...surface,
    resolving: surface.resolving || connecting,
    resolveError: surface.resolveError ?? authError ?? unavailable,
    connecting,
    linkError: link?.message ?? null,
    dismissLinkError: () => setLinkError(null),
    retryResolve,
  };
}
